/**
 * Stock Videos API
 *
 * Manage stock videos (public and admin listings), creation with uploads,
 * signed URLs, updates, deletes, and download tracking.
 */
import { get, post, put, del } from '../core';

/**
 * Gets public stock videos for landing page (no authentication required)
 * @returns {Promise<{stockVideos: Array}>} 6 random stock videos for carousel
 */
export function getPublicStockVideos() {
  return get('/publicStockVideos');
}

/**
 * Generates stock video data using AI based on a prompt
 * @param {string} prompt - The video description prompt
 * @returns {Promise<{status: string, stockVideoData: Object, message?: string}>} AI generated data
 */
export function generateStockVideoWithAI(prompt) {
  return post('/stockVideos/generate-stock-video', { prompt });
}

/**
 * Gets a stock video upload URL from the server
 * @returns {Promise<{uploadUrl: string, permanentUploadPath: string}>} Upload URL and permanent path
 */
export function getStockVideoUploadUrl() {
  return get('/stockVideos/video-upload-url');
}

/**
 * Creates a new stock video
 * @param {FormData} formData - The stock video form data
 * @returns {Promise<{message: string, stockVideoId: string}>} Success response
 */
export function createStockVideo(formData) {
  return post('/stockVideos/create', formData);
}

/**
 * Gets stock videos for premium users
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.tags - Comma-separated tags
 * @param {string} options.sortBy - Sort field
 * @returns {Promise<{stockVideos: Array, totalCount: number, currentPage: number, totalPages: number, hasMore: boolean}>} Stock videos data
 */
export function getStockVideos(options = {}) {
  const params = new URLSearchParams();
  
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search) params.append('search', options.search);
  if (options.tags) params.append('tags', options.tags);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  
  return get(`/stockVideos/stock?${params.toString()}`, { timeout: 20000 });
}

/**
 * Gets all stock videos for admin management
 * @param {Object} options - Query options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {string} options.search - Search term
 * @param {string} options.tags - Comma-separated tags
 * @param {string} options.sortBy - Sort field
 * @returns {Promise<{stockVideos: Array, totalCount: number, currentPage: number, totalPages: number}>} Stock videos data
 */
export function getAllStockVideosForAdmin(options = {}) {
  const params = new URLSearchParams();
  
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);
  if (options.search) params.append('search', options.search);
  if (options.tags) params.append('tags', options.tags);
  if (options.sortBy) params.append('sortBy', options.sortBy);
  
  return get(`/stockVideos/all?${params.toString()}`);
}

/**
 * Gets a single stock video by ID
 * @param {string} stockVideoId - The stock video ID
 * @returns {Promise<{stockVideo: Object}>} Stock video data
 */
export function getStockVideo(stockVideoId) {
  return get(`/stockVideos/${stockVideoId}`);
}

/**
 * Updates an existing stock video
 * @param {string} stockVideoId - The stock video ID
 * @param {FormData} formData - The form data containing updated fields
 * @returns {Promise<{message: string}>} Success response
 */
export function updateStockVideo(stockVideoId, formData) {
  return put(`/stockVideos/${stockVideoId}`, formData);
}

/**
 * Gets a signed URL for viewing a stock video
 * @param {string} stockVideoId - The stock video ID
 * @returns {Promise<{signedUrl: string, title: string, expiresAt: string}>} Signed URL data
 */
export function getStockVideoSignedUrl(stockVideoId) {
  return get(`/stockVideos/${stockVideoId}/signed-url`);
}

/**
 * Deletes a stock video
 * @param {string} stockVideoId - The stock video ID
 * @returns {Promise<{message: string}>} Success response
 */
export function deleteStockVideo(stockVideoId) {
  return del(`/stockVideos/${stockVideoId}`);
}

/**
 * Record stock video download when a user downloads a stock video
 * @param {string} stockVideoId - The ID of the stock video being downloaded
 * @returns {Promise<Object>} - The success response
 */
export async function downloadStockVideo(stockVideoId) {
  try {
    return await post(`/stockVideos/download/${stockVideoId}`, {});
  } catch (error) {
    console.error('Error recording stock video download:', error);
    // Don't throw the error - this is a non-critical operation
    return null;
  }
} 